import { customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useState } from "react";
import { MubinsoMapPinCard, MubinsoRegionCircleCard } from "./MubinsoMapPins";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import {
  MubinsoChecklistCard,
  MubinsoDetailListCard,
  MubinsoIconGrid,
  MubinsoNumberedIconCard,
  MubinsoReviewCarousel,
  MubinsoReportPreviewCard,
  MubinsoReceiptCard,
  MubinsoCtaButton,
  MubinsoGoiTalk,
  MubinsoKeepAll,
  MubinsoOrange as Orange,
  MubinsoScreenLayout,
  MubinsoStepEyebrow,
  MubinsoStepIntro as StepIntro,
} from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import { MubinsoEntry, mubinsoEntryTrigger } from "./mubinsoConstants";
import { MUBINSO_E4_LEAD_MODAL_PROPS } from "./mubinsoE4Constants";

const TOTAL_STEPS = 7;

/**
 * 무빈소 비용 퍼널 E4(신뢰 구간 개편). 현행 MubinsoCostFunnelA 의 처치군이다.
 *
 * 비용이 드는 세 곳(장례식장 · 화장·안장 · 상조)을 하나씩 보여 주고 → 장례 중에 비용이 늘어나는 이유 →
 * 상조를 고를 때 물어볼 3가지 → 고이는 그 3가지를 지킨다 → 결과 화면.
 * Figma 의 「합쳐도 일반 장례의 1/3」 화면은 퍼널을 줄이려고 뺐다(주완 2026-10-30).
 *
 * 문구 · 순서는 Figma 「C퍼널 = 비용 소구」(21830:19742 안, 22564:23353 부터)가 기준이다(맞춤법만 고쳤다).
 * 지금은 틀이다. 화면 모양은 기존 무빈소 컴포넌트로 우선 맞췄고, 화면별 디자인은 따로 한다.
 * 명세: wiki/plans/mubinso-funnel-trust.md
 */
export default function MubinsoCostFunnelE4({
  topicAnswer,
  onComplete,
  onBack,
  entry,
}: {
  /** C-1 의 「이전」. 퍼널 밖(E4 첫 화면)으로 돌아간다 */
  onBack: () => void;
  /** 첫 화면 버튼 답. E4 에는 Q2(상황)가 없어 상황은 결과 모달에서 받는다 */
  topicAnswer: string;
  onComplete: () => void;
  /** 진입 구분(topic_cost). 퍼널별 개별 이벤트(cst-cta_main 등) 발화에 쓴다 */
  entry: MubinsoEntry;
}) {
  const [step, setStep] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 단계 전환 시 스크롤 최상단 + 퍼널별 진행 이벤트. 현행(cost)과 단계 구성이 달라 funnel 값을 나눈다
  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({
      newGtm: { action: "mubinso_funnel_step", properties: { funnel: "cost_e4", step } },
    });
  }, [step]);

  const goNext = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-next-${step}` } });
    setStep((s) => s + 1);
  };

  const goPrev = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-prev-${step}` } });
    if (step === 1) onBack();
    else setStep((s) => s - 1);
  };

  const stepLayout = (current: number) => ({
    progress: { current, total: TOTAL_STEPS },
    onPrev: goPrev,
    footer: <MubinsoCtaButton label="다음" onClick={goNext} />,
  });

  // C-1 비용은 세 곳
  if (step === 1) {
    return (
      <MubinsoScreenLayout {...stepLayout(1)}>
        <StepIntro
          title={
            <>
              무빈소 장례의 <Orange>비용은</Orange>
              <br />
              <Orange>3가지</Orange>로 나누어져요.
            </>
          }
          // 아래 3칸이 장례식장 · 상조 · 화장장을 보여 줘 Figma 첫 문장을 빼고 두 줄로(주완 2026-10-30, 형식 님 확인 필요).
          // 윗줄이 더 길게(204 / 178px)
          desc={
            <>
              세 곳에 각각 어떤 비용이 드는지,
              <br />
              고이가 하나씩 알려드릴게요.
            </>
          }
        />
        <Spacing gap={24} />
        {/* 시안 2b(주완 2026-10-30): 「3가지」 가 세 덩어리로 바로 보이게 가로 3칸 + 무채색 번호.
            이름은 짧게(「화장장 · 납골당」 → 「화장장」, 「입관 용품」 → 「용품」), 납골당은 설명에 남긴다 */}
        <MubinsoNumberedIconCard
          items={[
            { icon: "icon_Graphic_Building", label: "장례식장", desc: "안치실 · 입관실\n사용료" },
            // 「화장장」 은 시설 이름이라 비용 이름 「화장·안장」 으로(2026-10-30 의견). 「안치」 는 C-2 안치실과 헷갈려 쓰지 않는다
            {
              icon: "icon_Graphic_BuildingRoof",
              label: "화장·안장",
              desc: "화장 · 납골당\n이용료",
            },
            { icon: "icon_Graphic_Suit", label: "상조", desc: "장례지도사\n용품 · 차량 비용" },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk question="무빈소인데 왜 장례식장에 비용을 내나요?">
          무빈소 장례는 빈소만 이용하지 않고, <b>안치, 입관, 발인과 같은 절차는 완전히 동일</b>
          하기 때문에 장례식장에 안치실 · 입관실 사용료를 지불해야 해요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-2 장례식장 비용
  if (step === 2) {
    return (
      <MubinsoScreenLayout {...stepLayout(2)}>
        {/* C-1 의 비용 3가지 중 몇 번째인지(B 절차 퍼널 ① ② 와 같은 모양, 주완 2026-10-30) */}
        <MubinsoStepEyebrow index={1} label="비용 · 장례식장" small />
        <Spacing gap={10} />
        <StepIntro
          title={
            <>
              <Orange>장례식장 비용</Orange>은
              <br />
              장례식장에 따라 달라져요.
            </>
          }
          desc={
            <MubinsoKeepAll>
              장례식장에 따라 무빈소 장례를 받지 않기도 해요. 무빈소 장례에는 빈소·접객·음식 비용이
              없지만, 안치실·입관실 사용료가 들어요.
            </MubinsoKeepAll>
          }
        />
        <Spacing gap={24} />
        {/* 시안 2(주완 2026-10-30): B-3 지도 카드와 같은 핀에 말풍선에 「무빈소 70만 원」 / 「무빈소 불가능」 을 넣는다(주완 2026-10-30). 지도 그림만 B-3 과 다르게.
            가격은 예시(Figma, 근거 확인 필요). C 는 「불가」 로 흐리게 */}
        {/* 카드 제목: 「무빈소가 가능한 장례식장인지 확인」 은 비용 화면과 어긋나 「현황」 으로(주완 2026-10-30, 형식 님 확인).
            금액이 예시라 실제 지역 이름 대신 「우리 동네」. 결과 화면 「우리 동네 무빈소 장례식장과 …」 로 이어진다 */}
        <MubinsoMapPinCard
          title="우리 동네 장례식장 현황"
          mapVariant="b"
          // 전국 1,067곳 무빈소 예상 비용(안치료 · 수시비 · 입관실 · 쓰레기 수거료, parlors_detail expected_charge_sum)
          // 평균 88 · 가운데 87만 원, 64% 가 70~100만 원(2026-10-30 확인). 「선」 을 빼야 360px 에서 한 줄(234 / 248px)
          footer="무빈소 장례식장 이용료 평균 70~100만 원"
          pins={[
            {
              icon: "icon_Graphic_Building",
              label: "A 장례식장",
              available: true,
              bubble: "무빈소 70만 원",
              x: 30,
              y: 14,
            },
            {
              icon: "icon_Graphic_BuildingLow",
              label: "B 장례식장",
              available: true,
              bubble: "무빈소 120만 원",
              // 말풍선이 길어져 A · C 와 겹치지 않게 엇갈려 둔다(360px 에서 끝 여백 20px 이상)
              x: 72,
              y: 70,
            },
            {
              icon: "icon_Graphic_BuildingRoof",
              label: "C 장례식장",
              available: false,
              bubble: "무빈소 불가능",
              x: 26,
              y: 128,
            },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          장례식장은 빈소와 음식에서 수익을 얻어요. 빈소가 없는 무빈소 장례는 장례식장 입장에서{" "}
          <b>수익이 적기 때문에 진행하지 않는 경우가 많아요.</b> 그래서{" "}
          <b>미리 가능한 장례식장을 확인</b>해두는 것이 좋아요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-3 화장·안장 비용. 상조를 마지막에 두어 C-5 「하지만 … 늘어나기도 해요」 와 바로 잇는다(주완 2026-10-30)
  if (step === 3) {
    return (
      <MubinsoScreenLayout {...stepLayout(3)}>
        <MubinsoStepEyebrow index={2} label="비용 · 화장·안장" small />
        <Spacing gap={10} />
        <StepIntro
          title={
            <>
              <Orange>화장·안장 비용</Orange>은
              <br />
              내가 고른 방식에 따라 달라져요.
            </>
          }
          desc={
            <>
              {/* 「권내 · 권외」 를 카드와 같은 「주소지 지역 내외」 로. 윗줄이 더 길게 */}
              화장, 납골당, 수목장 등 내가 고른 방식과
              <br />
              주소지 지역 내외에 따라 달라져요.
            </>
          }
        />
        <Spacing gap={24} />
        {/* 시안 4(주완 2026-10-30): 권내 · 권외를 「고인 주소지 지역 안 · 밖」 그림으로. 금액은 Figma(근거 확인 필요) */}
        <MubinsoRegionCircleCard
          // 「권내 · 권외」 대신 고객이 바로 아는 말로(주완 2026-10-30, 형식 님 확인). 뜻풀이 줄은 뺐다
          title="화장은 주소지 지역 내외가 중요해요."
          centerLabel="고인 주소지"
          inside="지역 내 10만 원 선"
          outside="지역 외 100만 원 선"
          // 360px 에서 한 줄(227px)
          footer="지역 내 화장장이 없으면 지자체 지원 확인"
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          화장장은 <b>주소지 지역 내와 지역 외의 가격 차이</b>가 큰 편이에요. 지역 내 화장장이 없는
          경우, 지자체에서 비용 일부를 지원하는 경우도 있어요. 그 때문에{" "}
          <b>진행 전 예약과 지자체 지원을 미리 확인하는 것이 중요</b>해요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-4 상조 비용
  if (step === 4) {
    return (
      <MubinsoScreenLayout {...stepLayout(4)}>
        <MubinsoStepEyebrow index={3} label="비용 · 상조" small />
        <Spacing gap={10} />
        <StepIntro
          title={
            <>
              <Orange>상조 비용</Orange>은 내가 고른
              <br />
              인력·용품·차량에 따라 달라져요.
            </>
          }
          desc={
            <MubinsoKeepAll>
              장례지도사, 관과 수의 같은 장례용품,
              <br />
              장의 차량 종류와 이동 거리에 따라 달라져요.
            </MubinsoKeepAll>
          }
        />
        <Spacing gap={24} />
        {/* 인력 · 용품 · 차량만(주완 2026-10-30): 이 구간은 업계 비용 설명이라 「고이가 해 드려요」 는 뒤 C-6 · C-7 에서.
            줄은 장례 진행 순서: 이송 차량(맨 위) → 인력 → 용품 → 장의 차량(맨 아래). 「무엇일수록 ↑」 는 뺐다(주완).
            이송 차량은 새로 그린 앰뷸런스 그래픽 아이콘. 카드 머리는 C-1 「비용이 들어가는 곳」 과 같은 말로 */}
        <MubinsoDetailListCard
          title="상조 비용이 들어가는 곳"
          // 항목별 금액은 뺐다. 네 줄 하한 합(140만 원)이 고이 상품가(159만 원)와 비교돼 비싸 보일 수 있다(주완 2026-10-30)
          rows={[
            {
              icon: "icon_Graphic_Ambulance",
              label: "이송 차량",
              detail: "앰뷸런스",
            },
            {
              icon: "icon_Graphic_Suit",
              label: "인력",
              detail: "장례지도사 · 입관상례사",
            },
            {
              icon: "icon_Graphic_Urn",
              label: "용품",
              detail: "관 · 수의 · 유골함",
            },
            {
              icon: "icon_Graphic_Bus",
              label: "장의 차량",
              detail: "장의 버스 · 리무진",
            },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          장례식장에서 상조와 같은 것을 제공하기도 해요. 빈소 이용 시 할인해 준다고 하거나, 다른
          혜택을 준다고 하는 경우도 있어요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-5 늘어나는 비용
  if (step === 5) {
    return (
      <MubinsoScreenLayout {...stepLayout(5)}>
        {/* C-5 · C-6 도 상조 이야기라 ③ 을 잇는다. C-7(고이 약속)은 비용 설명이 아니라 붙이지 않는다(주완 2026-10-30) */}
        <MubinsoStepEyebrow index={3} label="비용 · 상조" small />
        <Spacing gap={10} />
        <StepIntro
          title={
            <>
              {/* C-4 「내가 고른 … 따라 달라져요」 에서 꺾이게 「하지만」(주완 2026-10-30) */}
              하지만 장례를 하는 동안
              <br />
              <Orange>비용</Orange>이 늘어나기도 해요.
            </>
          }
          desc={
            <>
              장례를 하는 동안 많은 것들을 결정하면서
              <br />
              금액이 부풀려지는 경우가 많아요.
            </>
          }
        />
        <Spacing gap={24} />
        {/* 시안 5(주완 2026-10-30): 장례 중 붙는 것들을 영수증 「+ 추가」 줄로. 숫자 없이 「비용 ↑」 */}
        <MubinsoReceiptCard
          head="장례 비용 정산서"
          // 4줄: 붙는 게 많다는 게 보이게 관 · 수의와 유골함을 나눴다(주완 2026-10-30)
          items={[
            "비싼 관 · 수의 권유",
            "비싼 유골함 권유",
            "처음 안내에 없던 품목",
            "수고비 · 노잣돈 · 추가금",
          ]}
          totalLabel="최종 금액"
          totalValue="비용"
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          가시는 길에 조금이라도 더 좋은 걸 하고자 하는 마음, 슬픔과 같은 여러 감정을 이용해서 장례
          시장에서 관행적으로 <b>물건을 강매하거나 비용을 강요하는 경우</b>들이 있어요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-6 상조 고를 때 확인할 3가지. 「늘어나요(C-5) → 이걸 확인하세요 → 고이는 거부해요(C-7)」 순서(주완 2026-10-30).
  // Figma C-8 「쓴 만큼만 결제」 를 고객이 쓸 수 있는 체크리스트로 바꿨다. 고이 이야기는 다음 화면에서만 한다
  if (step === 6) {
    return (
      <MubinsoScreenLayout {...stepLayout(6)}>
        <MubinsoStepEyebrow index={3} label="비용 · 상조" small />
        <Spacing gap={10} />
        <StepIntro
          title={
            <>
              {/* C-5 「비용이 늘어나기도 해요」 를 「그래서」 로 받는다 */}
              그래서 상조를 고를 때는,
              <br />
              <Orange>이 3가지</Orange>를 꼭 물어보셔야 해요.
            </>
          }
          desc={
            <>
              하나라도 답을 듣지 못했다면,
              <br />
              계약을 다시 생각해 보는 게 좋아요.
            </>
          }
        />
        <Spacing gap={24} />
        <MubinsoChecklistCard
          icon="icon_Graphic_Examine"
          title="상조 계약 전 체크리스트"
          items={[
            { question: "가격을 미리 다 알려 주나요?", detail: "장례 중에 금액이 바뀌지 않는지" },
            { question: "안 쓴 품목은 빼고 계산하나요?", detail: "‘권리 포기’로 청구하지 않는지" },
            // 「장례 후 결제」 는 2번(안 쓴 품목 빼기)과 같은 이야기라 C-5 영수증의 수고비 · 노잣돈으로 바꿨다(주완)
            {
              question: "수고비 · 노잣돈을 따로 받나요?",
              detail: "장례 중에 추가금을 요구하지 않는지",
            },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          장례 시장에서는 관행적으로 <b>쓰지 않은 물품도</b> ‘권리 포기’, ‘다른 품목으로 대체’로
          간주하고 비용을 청구하는 경우가 많아요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // C-7 고이는 이 3가지를 지키고 환불까지 보장해요. 체크리스트(C-6) 다음, 결과 바로 앞
  if (step === 7) {
    return (
      <MubinsoScreenLayout {...stepLayout(7)}>
        {/* C-6 체크리스트 3가지에 답하는 화면 */}
        <StepIntro
          title={
            <>
              {/* 박스 4개 = 체크리스트 3가지 + 환불이라 「3가지를 지키고, 환불까지」(A안, 주완 2026-10-30) */}
              고이는 <Orange>이 3가지를 지키고,</Orange>
              <br />
              <Orange>환불까지 보장</Orange>해요.
            </>
          }
          desc={
            <>
              고이는 모든 품목을 가격정찰제로 운영하고,
              <br />
              관행적인 강매와 강요를 거부합니다.
            </>
          }
        />
        <Spacing gap={24} />
        {/* 2×2 회색 박스(영수증 C-5 와 다른 모양). Figma 정찰가 금액(55 · 75 · 55만 원)은 「숫자 줄이기」 가설
            (숫자가 많으면 성과가 낮음)로 뺐다 */}
        <MubinsoIconGrid
          // 앞 세 칸이 체크리스트 3가지에 하나씩 답한다. 설명은 360px 칸(124px)에 한 줄, 고객 말투로(주완 2026-10-30). 「돌려드려요」(123px)는 기기에 따라 두 줄이 돼 「환불해요」(113px)로
          // 문구 근거: 고이 원칙 「서비스 불만족시 그대로 환불해 드립니다」 「수고비를 주셔도 받지 않습니다」,
          // 홈 「불만족스러운 부분은 100% 환불해 드립니다」(HomeContainer)
          items={[
            {
              icon: "icon_Graphic_PriceList",
              title: "가격 미리 공개",
              desc: "모든 품목 가격정찰제",
            },
            {
              icon: "icon_Graphic_Examine",
              title: "안 쓴 품목은 빼요",
              desc: "쓴 만큼만 계산해요",
            },
            {
              icon: "icon_Graphic_Card",
              title: "수고비 · 노잣돈 없음",
              desc: "주셔도 받지 않아요",
            },
            {
              icon: "icon_Graphic_Shield",
              title: "불만족 시 환불 보장",
              desc: "마음에 안 들면 환불해요",
            },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          고이는 장례업계 최초로 가격정찰제를 실행했어요. 물품을 바꾸고 싶으시다면 바뀌는 모든
          금액을 미리 공유해 드려요. 고이에서 <b>물건을 강매하거나 강요하는 사례</b>가 있다면,{" "}
          <b>전액 환불</b>해 드립니다.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 결과. 영상은 뺐다(신청 버튼에서 눈을 돌리게 해서, 주완 2026-10-30). 리포트 카드 + 비용 후기
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        onPrev={goPrev}
        headline={
          <>
            {/* C-6 이 「그래서」 로 시작해 「그래서」 를 또 쓰지 않는다. 받을 것(비용과 정보)을 먼저 세운다(주완 2026-10-30) */}
            <Orange>구체적인 비용과 정보</Orange>,
            <br />
            고이에서 자세히 알려드릴게요.
          </>
        }
        sub={"내 상황에 맞는 예상 비용과\n추가로 생길 수 있는 항목까지 정리해 드려요."}
        checkItems={[]}
        checkCard={
          <BaseStack width="100%" spacing={16}>
            {/* B 결과와 같은 「정리해서 알려드려요」 카드. 신청하면 빈칸이 채워진다.
                「필요한 추가 항목 ? 개」 는 예상 장례 비용과 겹쳐 뺐다(주완 2026-10-31) */}
            <MubinsoReportPreviewCard
              title="고이가 정리해서 알려드려요"
              rows={[
                {
                  icon: "icon_Graphic_PriceList",
                  label: "예상 장례 비용",
                  sub: "내 상황 기준",
                  value: "? 원",
                },
                {
                  icon: "icon_Graphic_Building",
                  label: "무빈소 되는 장례식장",
                  sub: "내 지역 기준",
                  value: "? 곳",
                },
              ]}
            />
            {/* 비용 이야기 실제 후기만. 고이 후기 API(sangjo_reviews, 사이트 「고객후기」 와 같은 곳)의 원문 문장을
                그대로 잘라 썼다(맞춤법도 고치지 않음). 제목이 「무빈소」 라 운영 API product_type 이 무빈소인 후기만(주완 2026-10-31).
                이름은 운영 API 가 가려서 주는 값 그대로. 운영 후기 id: 2820 · 2531 · 2604 · 2474 · 2975.
                첫 장은 C-5 · C-6(추가 비용)과 가장 직접적인 후기 */}
            <MubinsoReviewCarousel
              title="무빈소로 장례를 치른 가족분들의 후기"
              onMove={(card, firstView) => {
                // 넘길 때마다 한 번(클릭 수) + 그 후기를 처음 볼 때 번호와 함께 한 번(몇 개까지 봤나)
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "cst-review" },
                });
                if (firstView) {
                  customEvent({
                    newGtm: { path: "mubinso_funnel", trigger_and_target: `cst-review-${card}` },
                  });
                }
              }}
              reviews={[
                {
                  text: (
                    <>
                      비용도 합리적인 편이었고, 처음 안내받은 금액뿐만 아니라{" "}
                      <b>추가적으로 발생할 수 있는 비용까지 자세하게 설명</b>해주셔서 나중에
                      예상하지 못한 비용 때문에 당황할 일도 없었습니다.
                    </>
                  ),
                  meta: "전OO 님 · 무빈소 · 2026.09",
                },
                {
                  text: (
                    <>
                      <b>정찰제로, 정확하게 예상된 금액으로만</b> 진행이 되었습니다.
                    </>
                  ),
                  meta: "이OO 님 · 무빈소 · 2026.08",
                },
                {
                  text: (
                    <>
                      무빈소상으로 하면서 <b>불필요한 것들은 전부 공제</b>를 다 해주셔서 경제적으로
                      덜 부담스럽게 해주었습니다.
                    </>
                  ),
                  meta: "김OO 님 · 무빈소 · 2026.08",
                },
                {
                  text: (
                    <>
                      신속하게 와주시고 <b>웃돈없이 깔끔하게</b> 무빈소 장례 치뤘습니다.
                    </>
                  ),
                  meta: "염OO 님 · 무빈소 · 2026.08",
                },
                {
                  text: (
                    <>
                      가격면에서도 합리적일 뿐만 아니라 <b>모든 과정이 투명하게 공개</b>되어 있어,
                      장례가 주는 중압감을 한결 덜고 아버지를 정성껏 모실 수 있었습니다.
                    </>
                  ),
                  meta: "최OO 님 · 무빈소 · 2026.10",
                },
              ]}
            />
          </BaseStack>
        }
        primaryLabel="구체적인 예상 비용 받아보기"
        onPrimary={() => {
          // 현행과 같은 이름으로 발화해야 A/B 를 같은 축에서 비교할 수 있다
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
            },
          });
          setIsModalOpen(true);
        }}
      />

      {/* Figma 「무료 정보 신청」(22640:21011). 숫자는 다른 화면과 같은 「건」 */}
      <MubinsoLeadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <MubinsoKeepAll>
            <Orange>구체적인 비용과 정보</Orange> 받아보기
          </MubinsoKeepAll>
        }
        description={
          <MubinsoKeepAll>
            고이가 2,000건 이상의 무빈소 장례 경험을 바탕으로, 필요하신 정보와 자료를 빠르게
            전해드릴게요.
          </MubinsoKeepAll>
        }
        buttonText="동의하고 무료로 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: "",
            topic: topicAnswer,
          })
        }
        {...MUBINSO_E4_LEAD_MODAL_PROPS}
        entry={entry}
        onSubmitted={onComplete}
      />
    </>
  );
}
